import type { ExportFormat } from "@cfg/kernel";
import type { ParamSpec } from "@cfg/design-sdk";
import { useCallback, useMemo, useState } from "react";
import { useConfiguratorContext, useConfiguratorStore } from "./context.tsx";
import { isDirty } from "./store.ts";

/** The loaded design's description (id, version, meta, ui, spec). */
export function useDesign() {
  const description = useConfiguratorStore((s) => s.description);
  const status = useConfiguratorStore((s) => s.status);
  const error = useConfiguratorStore((s) => s.error);
  const spec = useMemo(() => (description?.spec ?? []) as ParamSpec[], [description]);
  return { description, spec, status, error, loaded: description !== null };
}

/** Live parameters and setters. */
export function useParams() {
  const params = useConfiguratorStore((s) => s.params);
  const dirty = useConfiguratorStore((s) => isDirty(s));
  const { store } = useConfiguratorContext();
  const setParam = useCallback((name: string, value: unknown) => store.getState().setParams({ [name]: value }), [store]);
  const setParams = useCallback((patch: Record<string, unknown>) => store.getState().setParams(patch), [store]);
  const reset = useCallback(() => store.getState().reset(), [store]);
  const applyPreset = useCallback((id: string) => store.getState().applyPreset(id), [store]);
  const setActive = useCallback((name: string | null) => store.getState().setActiveParam(name), [store]);
  return { params, setParam, setParams, reset, applyPreset, dirty, setActive };
}

/** Current preview mesh, stats and print-check issues. */
export function useMesh() {
  const mesh = useConfiguratorStore((s) => s.mesh);
  const stats = useConfiguratorStore((s) => s.stats);
  const issues = useConfiguratorStore((s) => s.issues);
  const status = useConfiguratorStore((s) => s.status);
  const buildCount = useConfiguratorStore((s) => s.buildCount);
  return { mesh, stats, issues, building: status === "building" || status === "loading", status, buildCount };
}

export interface ExportState {
  busy: ExportFormat | null;
  error: string | null;
  last: { filename: string; bytes: number; format: ExportFormat } | null;
}

/** Export at print quality on the heavy worker and hand the file to the browser. */
export function useExport(onExported?: (info: { format: ExportFormat; filename: string; bytes: number }) => void) {
  const { store, client } = useConfiguratorContext();
  const [state, setState] = useState<ExportState>({ busy: null, error: null, last: null });

  const exportAs = useCallback(
    async (format: ExportFormat) => {
      const s = store.getState();
      if (!s.key) return;
      setState((st) => ({ ...st, busy: format, error: null }));
      try {
        const result = await client.export(s.key, s.params, format);
        downloadBlob(result.data, result.filename, format === "stl" ? "model/stl" : "model/3mf");
        const info = { format, filename: result.filename, bytes: result.data.byteLength };
        setState({ busy: null, error: null, last: info });
        onExported?.(info);
      } catch (err) {
        setState((st) => ({ ...st, busy: null, error: (err as Error).message }));
      }
    },
    [client, store, onExported],
  );

  return { ...state, exportStl: () => exportAs("stl"), export3mf: () => exportAs("3mf"), exportAs };
}

export function downloadBlob(data: ArrayBuffer | Uint8Array, filename: string, type: string): void {
  if (typeof document === "undefined") return;
  const blob = new Blob([data as BlobPart], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Migrate params saved under an older version to the loaded design's version. */
export function useMigrate() {
  const { client, store } = useConfiguratorContext();
  return useCallback(
    async (fromVersion: string, params: Record<string, unknown>) => {
      const s = store.getState();
      if (!s.key) throw new Error("No design loaded");
      const migrated = await client.migrate(s.key, fromVersion, params);
      s.replaceParams(migrated);
      return migrated;
    },
    [client, store],
  );
}
